(function () {
  'use strict';

  var API_URL = 'https://trustpilot.vivolife.com/api';
  var PER_PAGE = 20; // was 20 — smaller first page returns faster; loadMoreReviews() already backfills the rest as the customer scrolls
  var MOBILE_TEXT_BREAKPOINT = 768; // character-limit breakpoint (matches source component)
  var CACHE_VERSION = 'v1'; // bump this if the cached data shape ever changes, to invalidate old sessionStorage entries

  var JQUERY_SRC = 'https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js';
  var SLICK_JS_SRC = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js';
  var SLICK_CSS_SRC = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css';

  /* ---------------------------------------------------------------------
   * Dependency loading: the source Vue component relied on vue-slick +
   * jQuery being bundled by the app's build step. There's no such build
   * step here, so we load jQuery + slick from a CDN on demand, but skip
   * anything already present on the page (in case the theme already
   * loads jQuery elsewhere) so we never load either twice.
   *
   * FIX: loadStyle() now returns a Promise resolved on the stylesheet's
   * own 'load' event, and ensureDependencies() awaits it alongside the
   * scripts. Previously the CSS was fire-and-forget — slick() could run
   * and cards could render before slick.css had actually applied, which
   * is exactly the kind of race that produces a briefly (or, on a slow
   * connection, not-so-briefly) broken-looking carousel.
   * ------------------------------------------------------------------- */

  function loadStyle(href) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('link[data-vp-trustpilot-dep="' + href + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () { resolve(); });
          existing.addEventListener('error', function () { reject(new Error('Failed to load ' + href)); });
        }
        return;
      }
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-vp-trustpilot-dep', href);
      link.addEventListener('load', function () {
        link.setAttribute('data-loaded', 'true');
        resolve();
      });
      link.addEventListener('error', function () {
        reject(new Error('Failed to load ' + href));
      });
      document.head.appendChild(link);
    });
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-vp-trustpilot-dep="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () { resolve(); });
          existing.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
        }
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.setAttribute('data-vp-trustpilot-dep', src);
      script.addEventListener('load', function () {
        script.setAttribute('data-loaded', 'true');
        resolve();
      });
      script.addEventListener('error', function () {
        reject(new Error('Failed to load ' + src));
      });
      document.head.appendChild(script);
    });
  }

  function ensureDependencies() {
    var cssReady = loadStyle(SLICK_CSS_SRC);
    var jqReady = window.jQuery ? Promise.resolve() : loadScript(JQUERY_SRC);
    var scriptsReady = jqReady.then(function () {
      if (window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) return;
      return loadScript(SLICK_JS_SRC);
    });
    return Promise.all([cssReady, scriptsReady]);
  }

  /* ---------------------------------------------------------------------
   * Star rendering
   * ------------------------------------------------------------------- */

  function starFillState(rating, position) {
    var stars = Math.min(5, Math.max(0, rating || 0));
    if (stars >= position) return 'full';
    if (stars >= position - 0.5) return 'half';
    return 'empty';
  }

  var STAR_SHAPES = [
    { canvas: 'M0 46.330002h46.375586V0H0z', half: 'M23.187793 46.330002h23.187793V0H23.187793z', shape: 'M39.533936 19.711433L13.230239 38.80065l3.838216-11.797827L7.02115 19.711433h12.418975l3.837417-11.798624 3.837418 11.798624h12.418975zM23.2785 31.510075l7.183595-1.509576 2.862114 8.800152L23.2785 31.510075z' },
    { canvas: 'M51.24816 46.330002h46.375587V0H51.248161z', half: 'M74.435953 46.330002h23.187793V0H74.435953z', shape: 'M74.990978 31.32991L81.150908 30 84 39l-9.660206-7.202786L64.30279 39l3.895636-11.840666L58 19.841466h12.605577L74.499595 8l3.895637 11.841466H91L74.990978 31.329909z' },
    { canvas: 'M102.532209 46.330002h46.375586V0h-46.375586z', half: 'M125.720002 46.330002h23.187793V0h-23.187793z', shape: 'M142.066994 19.711433L115.763298 38.80065l3.838215-11.797827-10.047304-7.291391h12.418975l3.837418-11.798624 3.837417 11.798624h12.418975zM125.81156 31.510075l7.183595-1.509576 2.862113 8.800152-10.045708-7.290576z' },
    { canvas: 'M153.815458 46.330002h46.375586V0h-46.375586z', half: 'M177.003251 46.330002h23.187793V0h-23.187793z', shape: 'M193.348355 19.711433L167.045457 38.80065l3.837417-11.797827-10.047303-7.291391h12.418974l3.837418-11.798624 3.837418 11.798624h12.418974zM177.09292 31.510075l7.183595-1.509576 2.862114 8.800152-10.045709-7.290576z' },
    { canvas: 'M205.064416 46.330002h46.375587V0h-46.375587z', half: 'M228.252209 46.330002h23.187793V0h-23.187793z', shape: 'M244.597022 19.711433l-26.3029 19.089218 3.837419-11.797827-10.047304-7.291391h12.418974l3.837418-11.798624 3.837418 11.798624h12.418975zm-16.255436 11.798642l7.183595-1.509576 2.862114 8.800152-10.045709-7.290576z' }
  ];

  function renderStarsSvg(rating) {
    var groups = STAR_SHAPES.map(function (star, index) {
      var fill = starFillState(rating, index + 1);
      var canvasClass = 'vp-trustpilot__star-canvas' + (fill !== 'empty' ? ' vp-trustpilot__star-canvas--filled' : '');
      var halfMarkup = fill === 'half'
        ? '<path class="vp-trustpilot__star-canvas" d="' + star.half + '"></path>'
        : '';
      return (
        '<g>' +
          '<path class="' + canvasClass + '" d="' + star.canvas + '"></path>' +
          halfMarkup +
          '<path class="vp-trustpilot__star-shape" fill="#FFF" d="' + star.shape + '"></path>' +
        '</g>'
      );
    }).join('');

    return '<svg role="img" viewBox="0 0 251 46" xmlns="http://www.w3.org/2000/svg">' + groups + '</svg>';
  }

  var ARROW_PATH = 'M10.5088835 12l3.3080582-3.02451041c.2440777-.22315674.2440777-.5849653 0-.80812204-.2440776-.22315673-.6398058-.22315673-.8838834 0L9.18305826 11.595939c-.24407768.2231567-.24407768.5849653 0 .808122l3.75000004 3.4285714c.2440776.2231568.6398058.2231568.8838834 0 .2440777-.2231567.2440777-.5849653 0-.808122L10.5088835 12z';

  function arrowHtml(direction) {
    var rotate = direction === 'next' ? " style='transform: rotate(180deg);'" : '';
    return (
      "<div class='slick-" + direction + "'>" +
        "<svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'" + rotate + ">" +
          "<circle class='arrow-slider-circle' cx='12' cy='12' r='11.5'></circle>" +
          "<path class='arrow-slider-shape' d='" + ARROW_PATH + "'></path>" +
        '</svg>' +
      '</div>'
    );
  }

  /* ---------------------------------------------------------------------
   * Widget
   * ------------------------------------------------------------------- */

  function TrustpilotReviews(root, depsPromise) {
    this.root = root;
    this.depsPromise = depsPromise; // jQuery/slick loading — resolved in parallel with this widget's own data fetch, awaited only right before initSlick()
    this.apiKey = root.dataset.apiKey || '';
    this.businessUnitId = root.dataset.businessUnitId || '';
    this.skus = (root.dataset.skus || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    this.locale = root.dataset.locale || '';
    this.language = root.dataset.language || 'en';
    this.logoUrl = root.dataset.logoUrl || '';
    this.productIds = (root.dataset.productIds || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);

    var translationsNode = root.querySelector('[data-vp-trustpilot-translations]');
    try {
      this.translations = translationsNode ? JSON.parse(translationsNode.textContent) : {};
    } catch (err) {
      this.translations = {};
    }

    this.els = {
      ratingLineDesktop: root.querySelector('[data-vp-rating-line]'),
      starsDesktop: root.querySelector('[data-vp-stars-desktop]'),
      basedOnDesktop: root.querySelector('[data-vp-based-on-desktop]'),
      logo: root.querySelector('[data-vp-logo]'),
      logoFallback: root.querySelector('[data-vp-logo-fallback]'),
      ratingLineMobile: root.querySelector('[data-vp-rating-line-mobile]'),
      starsMobile: root.querySelector('[data-vp-stars-mobile]'),
      basedOnMobile: root.querySelector('[data-vp-based-on-mobile]'),
      stateLoading: root.querySelector('[data-vp-state-loading]'),
      stateError: root.querySelector('[data-vp-state-error]'),
      stateEmpty: root.querySelector('[data-vp-state-empty]'),
      viewport: root.querySelector('[data-vp-viewport]'),
      track: root.querySelector('[data-vp-track]')
    };

    this.reviews = [];
    this.totalReviews = 0;
    this.overallRating = 0;
    this.currentPage = 1;
    this.hasMoreReviews = true;
    this.loadingMore = false;

    if (this.logoUrl && this.els.logo) {
      this.els.logo.addEventListener('error', function () {
        this.els.logo.hidden = true;
        if (this.els.logoFallback) this.els.logoFallback.hidden = false;
      }.bind(this));
    }

    this.init();
  }

  TrustpilotReviews.prototype.t = function (key, fallback, replacements) {
    var resolved = key.split('.').reduce(function (acc, segment) {
      if (acc && typeof acc === 'object' && segment in acc) return acc[segment];
      return undefined;
    }, this.translations);
    var template = typeof resolved === 'string' ? resolved : (fallback || '');
    if (!replacements) return template;
    return Object.keys(replacements).reduce(function (acc, rKey) {
      return acc.replace(new RegExp('\\{' + rKey + '\\}', 'g'), replacements[rKey]);
    }, template);
  };

  TrustpilotReviews.prototype.characterLimit = function () {
    return window.innerWidth <= MOBILE_TEXT_BREAKPOINT ? 80 : 120;
  };

  TrustpilotReviews.prototype.sanitizeValue = function (value) {
    if (value === null || value === undefined) return '';
    var str = typeof value === 'string' ? value : String(value);
    if (str.trim().toLowerCase() === 'null') return '';
    return str;
  };

  TrustpilotReviews.prototype.truncateText = function (text) {
    var normalized = this.sanitizeValue(text);
    var limit = this.characterLimit();
    if (!normalized) return '';
    if (normalized.length <= limit) return normalized;
    return normalized.substring(0, limit) + '...';
  };

  TrustpilotReviews.prototype.formatCount = function (value) {
    var count = Number(value) || 0;
    return count.toLocaleString();
  };

  TrustpilotReviews.prototype.reviewLabel = function (count) {
    return count === 1
      ? this.t('summary.reviewSingular', 'review')
      : this.t('summary.reviewPlural', 'reviews');
  };

  TrustpilotReviews.prototype.formatReviewSummary = function (count) {
    var normalized = Number(count);
    var safeCount = isFinite(normalized) ? normalized : 0;
    return this.formatCount(safeCount) + ' ' + this.reviewLabel(safeCount === 1 ? 1 : safeCount);
  };

  TrustpilotReviews.prototype.combinedReviewCount = function () {
    return this.totalReviews > 0 ? this.totalReviews : this.reviews.length;
  };

  TrustpilotReviews.prototype.averageRating = function () {
    if (this.overallRating > 0) return this.overallRating.toFixed(1);
    if (this.reviews.length === 0) return '0.0';
    var sum = this.reviews.reduce(function (acc, r) { return acc + (r.rating || 0); }, 0);
    return (sum / this.reviews.length).toFixed(1);
  };

  TrustpilotReviews.prototype.formatRelativeDate = function (value) {
    // NOTE: kept as a plain localized date, matching the source component's
    // formatRelativeDate implementation. The "relativeTime" translation keys
    // (yearOne/monthOne/etc.) aren't actually consumed anywhere in that
    // implementation either, so they're carried over here for parity/future
    // use but currently unused.
    if (!value) return '';
    var date = new Date(value);
    if (isNaN(date.getTime())) return value;
    var now = new Date();
    var options = { day: 'numeric', month: 'long' };
    if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    return date.toLocaleDateString(undefined, options);
  };

  TrustpilotReviews.prototype.isProductLevelReview = function (review) {
    return Boolean(review && typeof review === 'object' && review.sku);
  };

  TrustpilotReviews.prototype.applyProductVerificationFallback = function (review) {
    if (this.isProductLevelReview(review) && !review.isVerified) {
      return Object.assign({}, review, { isVerified: true });
    }
    return review;
  };

  TrustpilotReviews.prototype.setState = function (state) {
    // state: 'loading' | 'error' | 'empty' | 'content'
    this.els.stateLoading.hidden = state !== 'loading';
    this.els.stateError.hidden = state !== 'error';
    this.els.stateEmpty.hidden = state !== 'empty';
    this.els.viewport.hidden = state !== 'content';

    // Root starts with the `hidden` attribute set in the Liquid markup, so
    // nothing is visible until there's an actual outcome. Only reveal on
    // 'content' — errors stay silent (logged to console, section stays
    // hidden) and empty stays hidden too, matching the Vue version's
    // v-if="!error && !loading" (which rendered nothing on either).
    if (state === 'content') {
      this.root.hidden = false;
    }
  };

  /* ---------------------------------------------------------------------
   * sessionStorage cache — keyed by which product(s)/language this widget
   * instance is actually showing reviews for, so different widgets (or
   * the same widget on a different product page) never collide. Only
   * the initial page of reviews + stats is cached (not paginated
   * loadMoreReviews() results) — this is specifically about skipping the
   * slow first round-trip on a repeat visit within the same session, not
   * a full offline review store.
   * ------------------------------------------------------------------- */

  TrustpilotReviews.prototype.cacheKey = function () {
    var identity;
    if (this.productIds.length > 0) {
      // Product IDs are always present on a real product, unlike SKUs —
      // which are optional merchant-entered data and can be blank on a
      // variant, silently falling through to the store-wide bucket below
      // and colliding with an actually-different store-wide section.
      identity = 'products:' + this.productIds.slice().sort().join(',');
    } else if (this.skus.length > 0) {
      identity = 'skus:' + this.skus.slice().sort().join(',');
    } else {
      identity = 'store-wide';
    }
    return 'vp-trustpilot:' + CACHE_VERSION + ':' + this.language + ':' + identity;
  };

  TrustpilotReviews.prototype.readCache = function () {
    try {
      var raw = sessionStorage.getItem(this.cacheKey());
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && Array.isArray(parsed.reviews) ? parsed : null;
    } catch (err) {
      // Private browsing (Safari throws on getItem in some versions) or
      // corrupted/incompatible cached JSON — treat exactly like a cache
      // miss rather than letting it break the widget.
      return null;
    }
  };

  TrustpilotReviews.prototype.writeCache = function () {
    try {
      sessionStorage.setItem(this.cacheKey(), JSON.stringify({
        totalReviews: this.totalReviews,
        overallRating: this.overallRating,
        reviews: this.reviews,
        hasMoreReviews: this.hasMoreReviews
      }));
    } catch (err) {
      // Private browsing / storage quota exceeded — caching is a
      // nice-to-have, never let it throw and take the widget down with it.
    }
  };

  TrustpilotReviews.prototype.logLoadTime = function (source) {
  };

  TrustpilotReviews.prototype.init = function () {
    this._loadStartTime = performance.now();
    this.els.stateLoading.textContent = this.t('loading', 'Loading reviews...');
    this.els.stateEmpty.textContent = this.t('noReviews', 'No reviews yet.');
    this.setState('loading');

    var cached = this.readCache();
    if (cached) {
      this.totalReviews = cached.totalReviews || 0;
      this.overallRating = cached.overallRating || 0;
      this.reviews = cached.reviews || [];
      this.hasMoreReviews = Boolean(cached.hasMoreReviews);
      // currentPage stays 1 — loadMoreReviews() still correctly continues
      // pagination from the cached first page onward.

      this.renderSummary();

      if (this.reviews.length === 0) {
        this.setState('empty');
        this.logLoadTime('cache, empty');
        return;
      }

      this.setState('content');
      this.els.track.innerHTML = this.reviews.map(function (review) {
        return this.buildCardHtml(review);
      }.bind(this)).join('');
      this.logLoadTime('cache');

      this.depsPromise.then(function () {
        this.initSlick();
      }.bind(this)).catch(function (err) {
        // Cards are already visible (state is 'content'); a slick failure
        // here just means the carousel behavior/arrows won't init. Log it,
        // don't hide anything or touch error state.
        console.error('Trustpilot widget: failed to init carousel from cache:', err);
      });

      return; // cache hit — skip the network fetch entirely for this initial load
    }

    // Stats, the review list, and jQuery/slick (this.depsPromise, kicked
    // off by bootstrap() before this widget was even constructed) are all
    // independent of each other — start everything in parallel instead of
    // making the review fetch wait on the CDN loads first, and render the
    // summary as soon as stats alone resolve rather than waiting on the
    // (often slower) full review fetch too.
    var statsPromise = this.fetchBusinessUnitStats().then(function () {
      this.renderSummary();
    }.bind(this));
    statsPromise.catch(function (err) {
      // Stats itself already swallows its own fetch errors (see
      // fetchBusinessUnitStats) — this only guards against renderSummary()
      // throwing for an unrelated reason, so it can't take the rest of
      // the widget down with it.
      console.warn('Trustpilot widget: summary render failed', err);
    });

    var reviewsPromise = this.fetchReviews(false);

    // Cache the result once both requests have actually settled — runs
    // alongside the rendering chain below, not gating it, so caching
    // never delays what's shown on screen.
    Promise.all([statsPromise, reviewsPromise]).then(function () {
      this.writeCache();
    }.bind(this)).catch(function () {
      // reviewsPromise rejected — nothing successful to cache.
    });

    reviewsPromise
      .then(function () {
        // Stats may still be in flight — combinedReviewCount() falls back
        // to reviews.length until totalReviews is available, so render
        // once more now in case stats hasn't resolved yet.
        this.renderSummary();

        if (this.reviews.length === 0) {
          this.setState('empty');
          this.logLoadTime('network, empty');
          return;
        }

        this.setState('content');
        this.els.track.innerHTML = this.reviews.map(function (review) {
          return this.buildCardHtml(review);
        }.bind(this)).join('');
        this.logLoadTime('network');

        // Cards are ready — the carousel itself just needs jQuery/slick,
        // which have very likely already finished loading in parallel by
        // now, so this rarely adds any real additional wait.
        return this.depsPromise.then(function () {
          this.initSlick();
        }.bind(this));
      }.bind(this))
      .catch(function (err) {
        // Anything unexpected here (fetch failure, bad translation key, a
        // missing DOM node, jQuery/slick failing to load, etc.). The
        // section was never revealed (root.hidden only clears on
        // 'content'), so it just stays hidden — no error message shown to
        // the customer, just logged for debugging.
        console.error('Trustpilot widget failed to load:', err);
        this.setState('error');
      }.bind(this));
  };

  TrustpilotReviews.prototype.buildParams = function (extra) {
    var params = new URLSearchParams(extra || {});
    if (this.skus.length > 0) params.set('sku', this.skus.join(','));
    return params;
  };

  TrustpilotReviews.prototype.fetchBusinessUnitStats = function () {
    var qs = this.buildParams().toString();
    return fetch(API_URL + '/stats' + (qs ? '?' + qs : ''))
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        if (data && data.success && data.stats) {
          this.totalReviews = Number(data.stats.totalReviews) || 0;
          this.overallRating = Number(data.stats.averageRating) || 0;
        }
      }.bind(this))
      .catch(function (err) {
        // Stats are a nice-to-have (average rating / review count); don't
        // let a stats failure block the reviews themselves from rendering.
        console.warn('Could not fetch Trustpilot stats:', err && err.message);
      });
  };

  TrustpilotReviews.prototype.fetchReviews = function (append) {
    var params = this.buildParams({
      limit: String(PER_PAGE),
      offset: String((this.currentPage - 1) * PER_PAGE)
    });
    if (this.language) params.set('language', this.language);

    return fetch(API_URL + '/reviews?' + params.toString())
      .then(function (response) {
        if (!response.ok) {
          throw new Error(this.t('errors.requestFailedWithStatus', 'Request failed with status {status}', { status: response.status }));
        }
        return response.json();
      }.bind(this))
      .then(function (payload) {
        if (!payload.success) {
          throw new Error(payload.error || this.t('errors.failedFetch', 'Failed to fetch reviews'));
        }
        var items = Array.isArray(payload.reviews) ? payload.reviews : [];
        var normalized = items.map(this.applyProductVerificationFallback.bind(this));

        this.reviews = append ? this.reviews.concat(normalized) : normalized;
        this.hasMoreReviews = items.length === PER_PAGE;
      }.bind(this));
    // Intentionally no top-level .catch here: a failed first fetch should
    // surface as the widget's error state via the .catch(...) chained in
    // init(). loadMoreReviews() (below) adds its own .catch for append
    // failures so pagination errors don't wipe out reviews already shown.
  };

  TrustpilotReviews.prototype.buildCardHtml = function (review) {
    var title = this.sanitizeValue(review.title);
    var titleHtml = title
      ? '<b class="vp-trustpilot__card-title">' + this.escapeHtml(title) + '</b><br>'
      : '';
    var verifiedHtml = review.isVerified
      ? (
        '<div class="vp-trustpilot__verified">' +
          '<span class="vp-trustpilot__verified-icon"></span>' +
          '<span>' + this.escapeHtml(this.t('badges.verified', 'Verified')) + '</span>' +
        '</div>'
      )
      : '';
    var dateHtml = review.createdAt
      ? '<div class="vp-trustpilot__reviewer-date">' + this.escapeHtml(this.formatRelativeDate(review.createdAt)) + '</div>'
      : '';

    return (
      '<div class="vp-trustpilot__card">' +
        '<div>' +
          '<div class="vp-trustpilot__card-inner">' +
            '<div class="vp-trustpilot__card-stars vp-trustpilot__stars">' + renderStarsSvg(review.rating) + '</div>' +
            '<p class="vp-trustpilot__card-text">' +
              titleHtml +
              this.escapeHtml(this.truncateText(review.text)) +
            '</p>' +
            '<div class="vp-trustpilot__reviewer">' +
              '<div>' +
                '<span class="vp-trustpilot__reviewer-name">' + this.escapeHtml(this.sanitizeValue(review.consumer && review.consumer.name)) + '</span>' +
                dateHtml +
                verifiedHtml +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  };

  TrustpilotReviews.prototype.escapeHtml = function (value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  };

  TrustpilotReviews.prototype.renderSummary = function () {
    var rating = this.averageRating();
    var starsHtml = renderStarsSvg(this.overallRating);
    var basedOnText = this.formatReviewSummary(this.combinedReviewCount());
    var ratedLabel = this.escapeHtml(this.t('ratedLabel', 'Rated {rating} / 5', { rating: rating }));

    if (this.els.ratingLineDesktop) {
      this.els.ratingLineDesktop.innerHTML = ratedLabel;
    }
    if (this.els.ratingLineMobile) {
      this.els.ratingLineMobile.innerHTML = ratedLabel;
    }
    if (this.els.starsDesktop) this.els.starsDesktop.innerHTML = starsHtml;
    if (this.els.starsMobile) this.els.starsMobile.innerHTML = starsHtml;
    if (this.els.basedOnDesktop) {
      this.els.basedOnDesktop.textContent = this.t('summary.basedOn', 'Based on') + ' ' + basedOnText;
    }
    if (this.els.basedOnMobile) {
      this.els.basedOnMobile.textContent = this.t('summary.basedOn', 'Based on') + ' ' + basedOnText;
    }
  };

  /* ---------------------------------------------------------------------
   * Carousel (real slick, matching the source component's config)
   * ------------------------------------------------------------------- */

  TrustpilotReviews.prototype.initSlick = function () {
    var $ = window.jQuery;
    this.$track = $(this.els.track);

    this.$track.on('afterChange', function (event, slick, currentSlide) {
      var slidesToShow = (slick.options && slick.options.slidesToShow) || 4;
      var slidesRemaining = this.reviews.length - currentSlide - slidesToShow;
      if (slidesRemaining <= 3 && this.hasMoreReviews && !this.loadingMore) {
        this.loadMoreReviews();
      }
    }.bind(this));

    this.$track.slick({
      responsive: [
        { breakpoint: 1320, settings: { slidesToShow: 3, slidesToScroll: 1 } },
        { breakpoint: 992, settings: { slidesToShow: 3, slidesToScroll: 1 } },
        { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 1 } }
      ],
      dots: false,
      cssEase: 'ease-in-out',
      slidesToShow: 4,
      slidesToScroll: 1,
      infinite: false,
      arrows: true,
      swipe: true,          // ← explicit, don't rely on default
      swipeToSlide: true,
      touchMove: true,       // ← explicit
      draggable: true,       // ← explicit (this is what powers desktop mouse-drag; touch is separate under the hood)
      touchThreshold: 8,      // ← lower = swipe registers with less horizontal travel
      prevArrow: arrowHtml('prev'),
      nextArrow: arrowHtml('next')
    });

    // If the initial page of reviews doesn't even fill one screen's worth
    // of slides, afterChange never fires (there's nothing to scroll), so
    // proactively check whether we should top up from the API.
    var currentSettings = this.$track.slick('getSlick').options || {};
    var initialSlidesToShow = currentSettings.slidesToShow || 4;
    if (this.reviews.length <= initialSlidesToShow && this.hasMoreReviews && !this.loadingMore) {
      this.loadMoreReviews();
    }
  };

  TrustpilotReviews.prototype.loadMoreReviews = function () {
    if (this.loadingMore || !this.hasMoreReviews) return;
    this.loadingMore = true;
    this.currentPage += 1;

    var previousCount = this.reviews.length;

    this.fetchReviews(true)
      .then(function () {
        var newReviews = this.reviews.slice(previousCount);
        newReviews.forEach(function (review) {
          this.$track.slick('slickAdd', this.buildCardHtml(review));
        }.bind(this));
      }.bind(this))
      .catch(function (err) {
        // Don't clear out reviews already shown just because pagination
        // failed; just stop trying to load more.
        console.warn('Trustpilot widget: failed to load more reviews', err);
      this.hasMoreReviews = false;
      }.bind(this))
      .finally(function () {
        this.loadingMore = false;
      }.bind(this));
  };

  /* ---------------------------------------------------------------------
   * Bootstrap — runs on window "load".
   * Dependencies (jQuery/slick) and each widget's own data fetch still
   * run in parallel: ensureDependencies() is kicked off once here and
   * handed to every widget instance, rather than each widget waiting for
   * it to resolve before even starting its own review/stats fetch.
   * ------------------------------------------------------------------- */

  // jQuery + slick (~127 KB) wait until a widget is within 800px of the
  // viewport; the reviews/stats fetch still starts straight away. Widgets are
  // re-queried on every check (not held) because the Vue app can re-render the
  // page after this script runs, leaving earlier nodes detached.
  var NEAR_MARGIN = 800;
  var nearPromise = null;

  function widgetNearViewport() {
    var roots = document.querySelectorAll('[data-vp-trustpilot]');
    for (var i = 0; i < roots.length; i++) {
      var rect = roots[i].getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;
      if (rect.top < window.innerHeight + NEAR_MARGIN && rect.bottom > -NEAR_MARGIN) return true;
    }
    return false;
  }

  function dependenciesWhenNear() {
    if (nearPromise) return nearPromise;
    nearPromise = new Promise(function (resolve) {
      var queued = false;
      function check() {
        queued = false;
        if (!widgetNearViewport()) return;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        window.removeEventListener('load', onScroll);
        resolve();
      }
      function onScroll() {
        if (queued) return;
        queued = true;
        setTimeout(check, 100);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      window.addEventListener('load', onScroll);
      onScroll();
    }).then(ensureDependencies);
    return nearPromise;
  }

  function bootstrap() {
    var depsPromise = dependenciesWhenNear();

    depsPromise.catch(function (err) {
      console.error('Trustpilot widget: failed to load jQuery/slick', err);
    });

    document.querySelectorAll('[data-vp-trustpilot]').forEach(function (root) {
      if (root.dataset.vpTrustpilotInitialized) return;
      root.dataset.vpTrustpilotInitialized = 'true';
      new TrustpilotReviews(root, depsPromise);
    });
  }

  // Run immediately — the [data-vp-trustpilot] markup is already in the DOM
  // (Shopify renders it server-side), so there's no need to wait for window
  // "load" just to start the reviews/stats fetch and the jQuery/slick tags.
  // Both are async network requests; they don't block or compete with
  // anything else on the page. This is what closes the gap with the Vue
  // version, which starts fetching as soon as it mounts rather than waiting
  // for every image on the page to finish.
  bootstrap();

  // Kept as a safety net for widgets whose markup shows up after this script
  // has already run — anything already initialized above is skipped via
  // vpTrustpilotInitialized, so this can't cause a double-init.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', bootstrap);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('[data-vp-trustpilot]') : null;
    if (root && !root.dataset.vpTrustpilotInitialized) {
      root.dataset.vpTrustpilotInitialized = 'true';
      new TrustpilotReviews(root, ensureDependencies());
    }
  });
})();